import { Component, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, Lightformer, Sparkles, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { Hotspot } from "./Hotspot";
import { Pour, curves, POUR_COLORS, POUR_TIME, TIP_X, TIP_Y } from "./Pour";
import { fizz } from "../../lib/audio";
import { HOTSPOTS } from "../../data/content";

export const R = 0.78;
const LABEL_H = 3.02;
const CAN_H = 3.75;
const TAU = Math.PI * 2;
const damp = THREE.MathUtils.damp;

const PROFILE = [
  [0, -1.6], [0.45, -1.62], [0.62, -1.7], [0.68, -1.75], [0.73, -1.72], [0.765, -1.62], [R, -1.5],
  [R, 1.5], [0.765, 1.6], [0.7, 1.73], [0.64, 1.82], [0.63, 1.86], [0.655, 1.88], [0.662, 1.91],
  [0.645, 1.93], [0.615, 1.91], [0.6, 1.87], [0, 1.86],
].map(([x, y]) => new THREE.Vector2(x, y));

function dropletMaps() {
  const w = 1024, h = 512;
  const mk = () => Object.assign(document.createElement("canvas"), { width: w, height: h });
  const bc = mk(), rc = mk();
  const b = bc.getContext("2d"), r = rc.getContext("2d");
  b.fillStyle = "#000"; b.fillRect(0, 0, w, h);
  r.fillStyle = "#b8b8b8"; r.fillRect(0, 0, w, h);
  for (let i = 0; i < 1500; i++) {
    const x = Math.random() * w, y = Math.random() * h;
    const s = Math.random() < 0.88 ? 0.7 + Math.random() * 2.2 : 3 + Math.random() * 4.5;
    const g = b.createRadialGradient(x - s * 0.3, y - s * 0.4, 0, x, y, s * 1.2);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    b.fillStyle = g;
    b.beginPath(); b.ellipse(x, y, s, s * 1.3, 0, 0, TAU); b.fill();
    r.fillStyle = "rgba(18,18,18,0.9)";
    r.beginPath(); r.ellipse(x, y, s * 0.9, s * 1.15, 0, 0, TAU); r.fill();
  }
  return [new THREE.CanvasTexture(bc), new THREE.CanvasTexture(rc)];
}

function Can({ variant, hot }) {
  const [orig, berry] = useTexture(["/assets/label-original-soon.jpg", "/assets/label-berry-soon.jpg"]);
  useMemo(() => [orig, berry].forEach((t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; }), [orig, berry]);
  const [bump, rough] = useMemo(dropletMaps, []);
  const lathe = useMemo(() => new THREE.LatheGeometry(PROFILE, 96), []);
  const [open, setOpen] = useState(null);
  useEffect(() => { if (!hot) setOpen(null); }, [hot]);
  return (
    <group>
      <mesh geometry={lathe}>
        <meshStandardMaterial color="#e4e7ea" metalness={1} roughness={0.28} side={THREE.DoubleSide} />
      </mesh>
      <mesh>
        <cylinderGeometry args={[R + 0.004, R + 0.004, LABEL_H, 128, 1, true, -Math.PI / 3, TAU]} />
        <meshPhysicalMaterial
          map={variant === "berry" ? berry : orig}
          roughness={0.62} roughnessMap={rough} metalness={0.12}
          bumpMap={bump} bumpScale={1.4} clearcoat={0.35} clearcoatRoughness={0.3}
        />
      </mesh>
      <mesh position={[0, 1.885, 0.16]} rotation={[-Math.PI / 2, 0, 0]}>
        <boxGeometry args={[0.2, 0.34, 0.016]} />
        <meshStandardMaterial color="#c7ccd0" metalness={1} roughness={0.18} />
      </mesh>
      <mesh position={[0, 1.875, 0]}>
        <cylinderGeometry args={[0.045, 0.045, 0.025, 24]} />
        <meshStandardMaterial color="#bfc4c8" metalness={1} roughness={0.25} />
      </mesh>
      {hot && HOTSPOTS.map((h) => (
        <Hotspot key={h.id} h={h} variant={variant} open={open} setOpen={setOpen} r={R} />
      ))}
    </group>
  );
}

function Rig() {
  const group = useRef();
  const inner = useRef();
  const s = useRef({ x: 0, y: -8, z: 0, scale: 0.001, rot: 0, spin: 0, user: 0, vel: 0, tilt: 0, tiltT: 0, mx: 0, my: 0, mxs: 0, mys: 0, drag: false, lx: 0, ly: 0, idle: 0, variant: "original", hot: false });
  const [variant, setVariant] = useState("original");
  const [hot, setHot] = useState(false);
  const [pouring, setPouring] = useState(false);
  const tipper = useRef();
  const pour = useRef({ t0: -100, color: POUR_COLORS.original });

  useEffect(() => {
    const st = s.current;
    const down = (e) => {
      const t = e.target;
      if (!(t instanceof Element) || !t.closest("[data-orbit]")) return;
      if (t.closest("a,button,input,textarea,select,label,[data-no-orbit]")) return;
      st.drag = true; st.lx = e.clientX; st.ly = e.clientY; st.vel = 0;
      document.body.classList.add("is-grabbing");
    };
    const move = (e) => {
      st.mx = (e.clientX / window.innerWidth) * 2 - 1;
      st.my = (e.clientY / window.innerHeight) * 2 - 1;
      if (!st.drag) return;
      const dx = e.clientX - st.lx, dy = e.clientY - st.ly;
      st.lx = e.clientX; st.ly = e.clientY;
      st.user += dx * 0.012; st.vel = dx * 0.012; st.idle = 0;
      st.tiltT = THREE.MathUtils.clamp(st.tiltT + dy * 0.004, -0.45, 0.45);
    };
    const up = () => {
      if (!st.drag) return;
      st.drag = false;
      document.body.classList.remove("is-grabbing");
    };
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  }, []);

  useFrame((state, delta) => {
    const st = s.current;
    const dt = Math.min(delta, 0.05);
    const vw = window.innerWidth, vh = window.innerHeight;
    const cam = state.camera;
    const unit = (2 * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)) * cam.position.z) / vh;
    let best = null, bestScore = 0;
    document.querySelectorAll("[data-can-stage]").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.height <= 0) return;
      const vis = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
      const score = vis / Math.min(r.height, vh);
      if (score > bestScore) { bestScore = score; best = { el, r }; }
    });
    let tx = st.x, ty = st.y, ts = 0, trot = 0, snap = false;
    if (best && bestScore > 0.3) {
      const { el, r } = best;
      tx = (r.left + r.width / 2 - vw / 2) * unit;
      ty = -(r.top + r.height / 2 - vh / 2) * unit;
      ts = Math.min((r.height * 0.84 * unit) / CAN_H, (r.width * 0.7 * unit) / (R * 2));
      trot = parseFloat(el.dataset.rot || "0");
      snap = el.dataset.snap === "1";
      const v = el.dataset.variant || "original";
      if (v !== st.variant) {
        if (st.hot && st.lastEl === el) {
          pour.current = { t0: state.clock.elapsedTime, color: POUR_COLORS[v] };
          st.pouring = true; setPouring(true); st.user = Math.round(st.user / TAU) * TAU; st.vel = 0;
          fizz(1.4); setTimeout(() => fizz(1.2), 900); setTimeout(() => fizz(0.9), 1800);
        } else st.vel += 0.32;
        st.variant = v; setVariant(v);
      }
      st.lastEl = el;
      const h = el.dataset.hotspots === "1";
      if (h !== st.hot) { st.hot = h; setHot(h); }
    } else if (st.hot) { st.hot = false; setHot(false); }

    const dist = Math.hypot(tx - st.x, ty - st.y);
    st.x = damp(st.x, tx, 3, dt);
    st.y = damp(st.y, ty, 3, dt);
    st.z = damp(st.z, -Math.min(3, dist * 0.7), 3, dt);
    st.scale = damp(st.scale, ts, 3.4, dt);
    if (!st.drag) {
      st.user += st.vel;
      st.vel *= Math.pow(0.93, dt * 60);
      st.idle += dt;
      if (snap && st.idle > 1.6 && Math.abs(st.vel) < 0.002) st.user = damp(st.user, Math.round(st.user / TAU) * TAU, 2.2, dt);
      st.tiltT = damp(st.tiltT, 0, 1.4, dt);
    }
    st.rot = damp(st.rot, trot, 4, dt);
    st.tilt = damp(st.tilt, st.tiltT, 6, dt);
    st.mxs = damp(st.mxs, st.mx, 2.5, dt);
    st.mys = damp(st.mys, st.my, 2.5, dt);
    const t = state.clock.elapsedTime;
    const T = t - pour.current.t0;
    const pc = curves(T);
    if (st.pouring && T > POUR_TIME) { st.pouring = false; setPouring(false); }
    const shrink = 1 - (vw < 640 ? 0.45 : 0.28) * pc.glass;
    const sc = Math.max(st.scale * shrink, 0.0001);
    const g = group.current;
    g.position.set(st.x - 0.3 * pc.glass * sc, st.y + Math.sin(t * 1.1) * 0.07 * st.scale, st.z);
    g.scale.setScalar(sc);
    tipper.current.rotation.z = -2 * pc.tip;
    tipper.current.position.set(TIP_X * pc.tip, TIP_Y * pc.tip, 0);
    const calm = 1 - pc.tip;
    g.visible = st.scale > 0.01;
    inner.current.rotation.set(
      0.06 + st.tilt + st.mys * 0.12 + Math.sin(t * 0.7) * 0.03,
      st.rot + st.user + (st.mxs * 0.35 + Math.sin(t * 0.45) * 0.18) * calm,
      -0.06 + Math.sin(t * 0.9) * 0.03
    );
  });

  return (
    <group ref={group}>
      <group ref={tipper}>
        <group ref={inner}>
          <Can variant={variant} hot={hot && !pouring} />
        </group>
      </group>
      <Pour pour={pour} />
      <Sparkles count={40} scale={[2.8, 4.4, 2.8]} size={2.4} speed={0.35} noise={0.6} color="#C7E6EA" opacity={0.85} />
    </group>
  );
}

class Boundary extends Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() {}
  render() { return this.state.failed ? null : this.props.children; }
}

export default function CanScene() {
  return (
    <Boundary>
      <Canvas
        data-testid="can-canvas"
        dpr={[1, 1.75]}
        camera={{ position: [0, 0, 10], fov: 30 }}
        gl={{ antialias: true, alpha: true }}
        onCreated={({ gl }) => { gl.toneMapping = THREE.NeutralToneMapping; gl.toneMappingExposure = 1.05; }}
        style={{ position: "fixed", inset: 0, zIndex: 20, pointerEvents: "none" }}
      >
        <ambientLight intensity={0.35} />
        <directionalLight position={[4, 5, 6]} intensity={1.6} />
        <directionalLight position={[-6, 1, -2]} intensity={1.2} color="#77C8D3" />
        <Suspense fallback={null}>
          <Environment resolution={256} frames={1}>
            <Lightformer intensity={2.2} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={[10, 10, 1]} />
            <Lightformer intensity={3} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={[20, 0.6, 1]} />
            <Lightformer intensity={2.4} rotation-y={-Math.PI / 2} position={[8, 1, 0]} scale={[20, 1, 1]} color="#C7E6EA" />
            <Lightformer intensity={1.5} position={[0, -4, 4]} scale={[10, 2, 1]} color="#007D6C" />
            <Lightformer intensity={2} position={[0, 3, 8]} scale={[8, 3, 1]} />
            <Lightformer intensity={1.6} rotation-x={-Math.PI / 2} position={[0, 8, 0]} scale={[6, 6, 1]} />
          </Environment>
          <Rig />
        </Suspense>
      </Canvas>
    </Boundary>
  );
}
